/**
 * SA Intake Rollup — Super Admin → More → Intake Rollup (READ-ONLY).
 *
 * Two cross-org tabs for support investigations (no mutation):
 *   1. validation-failures   → /api/super-admin/intake/validation-failures
 *   2. missing-bills-global  → /api/super-admin/intake/missing-bills-global
 *
 * URL: /super-admin/intake-rollup?tab=<tab-id>
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, ShieldAlert, MailWarning, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { isSuperAdmin } from '../../lib/auth';
import api from '../../lib/api';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const TABS = [
  { id: 'validation-failures', label: 'Validation Failures', icon: ShieldAlert },
  { id: 'missing-bills-global', label: 'Missing Bills (Global)', icon: MailWarning },
];

export default function IntakeRollupPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { if (!isSuperAdmin()) navigate('/dashboard'); }, [navigate]);

  const initial = useMemo(() => {
    const t = new URLSearchParams(location.search || '').get('tab');
    return TABS.find((d) => d.id === t)?.id || TABS[0].id;
  }, [location.search]);

  const [active, setActive] = useState(initial);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setActive(initial); }, [initial]);

  const setTab = (id) => {
    setActive(id);
    const sp = new URLSearchParams(location.search || '');
    sp.set('tab', id);
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = active === 'validation-failures'
        ? '/super-admin/intake/validation-failures'
        : '/super-admin/intake/missing-bills-global';
      const { data } = await api.get(url, { params: { limit: 300 } });
      setRows(data.items || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background" data-testid="sa-intake-rollup-page">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="h-6 w-6" strokeWidth={2} />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Super Admin
            </h1>
            <span className="text-muted-foreground">/</span>
            <span className="text-sm font-medium">Intake Rollup (read-only)</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin')} data-testid="back-to-super-admin">
            <ArrowLeft className="h-4 w-4 mr-2" /> Super Admin
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-6 space-y-5">
        <div className="flex gap-2">
          {TABS.map((t) => {
            const { Icon } = { Icon: t.icon };
            return (
              <Button key={t.id} variant={active === t.id ? 'default' : 'outline'} size="sm"
                onClick={() => setTab(t.id)} data-testid={`sa-intake-tab-${t.id}`}>
                <Icon className="h-4 w-4 mr-1" /> {t.label}
              </Button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : active === 'validation-failures' ? (
          <Table data-testid="sa-validation-failures-table">
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Account #</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Blocked</TableHead>
                <TableHead>Validated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No failures across orgs.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs font-mono">{r.account_id}</TableCell>
                  <TableCell><Badge variant="outline" className={r.overall_status === 'failed' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>{r.overall_status}</Badge></TableCell>
                  <TableCell>{r.vendor_name || '\u2014'}</TableCell>
                  <TableCell>{r.account_number || '\u2014'}</TableCell>
                  <TableCell>{r.invoice_number || '\u2014'}</TableCell>
                  <TableCell className="text-xs">{r.promotion_blocked_reason || '\u2014'}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.validated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table data-testid="sa-missing-bills-global-table">
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Account #</TableHead>
                <TableHead>Expected</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Flag</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No late/missing bills across orgs.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs font-mono">{r.account_id}</TableCell>
                  <TableCell>{r.property_name || '\u2014'}</TableCell>
                  <TableCell>{r.vendor_name}</TableCell>
                  <TableCell>{r.vendor_account_number}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.expected_issue_date)}</TableCell>
                  <TableCell><Badge variant="outline" className={r.severity === 'red' ? 'bg-red-100 text-red-700 border-red-200' : r.severity === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-200' : ''}>{r.days_past_expected}d</Badge></TableCell>
                  <TableCell><Badge variant="secondary">{r.flag}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
